import { useQuery } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

const FOUR_HOURS = 4 * 60 * 60 * 1000;

/**
 * @typedef {Object} Site
 * @property {number} id
 * @property {string} name
 * @property {string} zipCode
 * @property {boolean} isActive
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {Site} data - Self-referencing data property for component compatibility
 * @export
 */

/**
 * Custom hook to search for sites.
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {string} [params.zipCode]
 * @param {boolean} [params.isActive]
 * @param {boolean} [params.countOnly=false]
 * @param {boolean} [params.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult & { data: Site[], total: number, page: number, pageSize: number }}
 */
export default function useSiteSearch(params = {}) {
  const {
    page = 1,
    pageSize = 20,
    zipCode,
    isActive,
    countOnly = false,
    enabled = true,
  } = params;

  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;

  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('pageSize', pageSize);
  if (zipCode) queryParams.append('zipCode', zipCode);
  if (isActive !== undefined) queryParams.append('isActive', isActive);
  if (countOnly) queryParams.append('countOnly', countOnly);

  const query = useQuery({
    queryKey: ['sites', 'search', { page, pageSize, zipCode, isActive, countOnly }],
    queryFn: () => {
      const url = `${apiRoot}/core/sites/search?${queryParams.toString()}`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch sites');
        }
        return res.json();
      });
    },
    staleTime: FOUR_HOURS,
    cacheTime: FOUR_HOURS,
    enabled,
  });

  return {
    ...query,
    // Spread the original query properties and transform 'data'.
    // If data exists, map over the nested 'data' array to ensure each item
    // contains a self-referencing 'data' property for component compatibility.
    // Returns an empty array if query.data is not yet available.
    data: query.data && query.data.data ? query.data.data.map((site) => ({
      ...site,
      data: site
    })) : [],
    total: query.data ? query.data.total : 0,
    page: query.data ? query.data.page : page,
    pageSize: query.data ? query.data.pageSize : pageSize,
    city: query.data ? query.data.city : null,
    state: query.data ? query.data.state : null,
  };
};
